import {
  BrowserRouter as Router,
  Routes as ReactRoutes,
  Route as ReactRoute,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { TEAMS_ENABLED } from "./pages/Teams/flag";
import { WHEEL_COLLABORATION_ENABLED } from "./components/WheelCollaboration/featureFlag";
import { FOLLOWER_FEED_ENABLED } from "./components/FollowerFeed/featureFlag";
import { PrivateRoute, LoginRedirect } from "./PrivateRoute.js";
import { PATH } from "./utils/config.js";
import { getLoggedInUser, getToken, isUserLoggedIn } from "./utils/helpers.js";
import { CAPTURE_ENABLED } from "./constants/featureFlags";
import { HELP_UNIFIED_ENABLED } from "./constants/featureFlags";
import { HELP_UNIFIED_REDIRECTS } from "./pages/Help/helpUnified.logic";
import { CHAT_ENABLED } from "./constants/featureFlags.js";
import { RENDERER_V2_ENABLED } from "./constants/featureFlags";
import { AI_WHEEL_GENERATOR_ROUTE_ENABLED } from "./constants/featureFlags";
import { GALLERY_THEMING_ENABLED } from "./constants/featureFlags";
import { MODERATION_CONSOLE_ENABLED } from "./constants/featureFlags";
import { ACCOUNT_SWITCHER_ENABLED } from "./components/ProfileMenu/accountSwitcherFlag";
import { OPS_CONSOLE_ENABLED } from "./constants/featureFlags";
import { AI_WHEEL_LAB_ENABLED } from "./constants/featureFlags";
import { RESPONSES_SECTION_ENABLED } from "./constants/featureFlags";
import { MAGIC_LINK_LOGIN_ENABLED } from "./pages/Auth/MagicLink/magicLinkFlag.js";
import { useDispatch } from "react-redux";
import { getOwnProfileIfNeededAction } from "./redux/actions/user.js";
import { useSubscriptionSync } from "./hooks/useSubscriptionSync.js";
import BrandWheelLoader from "./components/BrandWheelLoader";
import PageErrorBoundary from "./components/PageErrorBoundary";
// Eager (tiny) — it is the /wheel/:wheelId route's Suspense fallback (T13.1).
import ViewWheelLoadingShell from "./pages/ViewWheel/ViewWheelLoadingShell";
import NonAuthenticatedModal from "./components/NonAuthenticatedModal";
// Deliberately a REGULAR (non-lazy) import — see MessagesRoutePrefetch's own
// header comment for why it must mount outside the lazy MessagesLayout chunk.
import MessagesRoutePrefetch from "./pages/Chat/MessagesRoutePrefetch.jsx";
// LOADER-STUTTER fix (ISP-9999, Gordon 2026-08-23): the two surfaces whose OWN
// data-loading affordance is NOT the fullpage brand wheel — Discover (a card
// skeleton grid) and Gallery (a hero wheel) — get a PER-ROUTE Suspense fallback
// that MATCHES that affordance (below), so the route-chunk download and the
// subsequent data load show the SAME thing. Without this, both suspended to the
// shared fullpage <BrandWheelLoader> and the page then swapped it for a different
// loader (wheel -> skeleton / fullpage -> hero) — the perceived multi-load
// stutter Gordon reported. See qa-notes/LOADER-STUTTER-AUDIT.md.
import GallerySkeleton from "./components/Gallery/GallerySkeleton";
import CollectionRedirect from "./components/WheelGallery/WheelListing/FoldersV2/CollectionRedirect";
// Login-loader remount fix: single stable owner of the gallery boot loader, so
// the chunk-load and data-load phases share ONE GallerySkeleton instance instead
// of tearing one down and rebuilding another (the reported 3-flash blink).
import { GalleryBootBoundary } from "./components/Gallery/GalleryBoot";
import DiscoverFeedSkeleton from "./components/ExploreV2/DiscoverFeedSkeleton";

// PERF-001 / WEB-008 (WL-069): route-based code-splitting.
// Every page component below is loaded via React.lazy so each compiles to its
// own webpack chunk instead of being bundled into main.*.js. Before this change
// the whole app (including the heavy Segment Builder, ViewWheel, Settings,
// Gallery, Explore) shipped in one ~4.7MB main bundle that EVERY route paid on
// first paint. The single <Suspense fallback={<CustomLoader />}> boundary around
// the route table (see below) renders the shared, on-brand loader while a route
// chunk downloads. Pure structural change: no route paths, guards, gates, or
// props are altered — only how each page module is imported.
const Signup = lazy(() => import("./pages/Auth/Signup"));
const Login = lazy(() => import("./pages/Auth/Login"));
const Settings = lazy(() => import("@pages/Settings"));
const Teams = lazy(() => import("@pages/Teams"));
// [legacy removed 2026-07-14] Profile (blue-header owner profile) — retired; routes redirect to /gallery.
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy/index.js"));
const Terms = lazy(() => import("./pages/Terms/index.js"));
// [legacy removed 2026-07-14] OthersProfile (blue-header public profile) — retired; routes redirect to /gallery.
const Invite = lazy(() => import("./pages/Invite"));
const SegmentBuilder = lazy(() => import("./pages/SegmentBuilder"));
const ViewWheel = lazy(() => import("./pages/ViewWheel/index.jsx"));
const CollaborationAccept = lazy(() => import("./pages/CollaborationAccept"));
const CollaborationJoin = lazy(() => import("./pages/CollaborationJoin"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const Gallery = lazy(() => import("./pages/Gallery/index.jsx"));
const MediaLibrary = lazy(() => import("./pages/MediaLibrary/index.jsx"));
const ForgotPassword = lazy(() =>
  import("./pages/Auth/ForgotPassword/index.jsx")
);
const RequestMagicLink = lazy(() =>
  import("./pages/Auth/MagicLink/RequestMagicLink.jsx")
);
const MagicLinkLogin = lazy(() =>
  import("./pages/Auth/MagicLink/MagicLinkLogin.jsx")
);
const Analytics = lazy(() => import("./pages/Referrals/index.jsx"));
// ISP-9999: QA-1014 (2026-08-24) redirected /notifications to Discover on the
// theory that the bell dropdown's infinite scroll was "the full list now."
// Measured on staging: the list API returns count=426 across 43 pages, but
// "See all" only ever expanded the SAME popover in place (no navigation, no
// modal) and the dropdown never loaded past a handful of pages in practice —
// ~416 notifications were unreachable by any path, live or bookmarked
// (/notifications 404'd into Discover). The full-list page below already
// existed on disk (unrouted) with its own working "See More" pagination —
// re-routing it is the fix, not writing a new page.
const NotificationsPage = lazy(() => import("./pages/Notifications/index.jsx"));
const LinkVaultPage = lazy(() => import("./pages/LinkVaultPage/index.jsx"));
const ExploreV2Page = lazy(() => import("./pages/ExploreV2/index.jsx"));
const FollowerFeedPage = lazy(() => import("./pages/FollowerFeed/index.jsx"));
const Onboarding = lazy(() => import("./pages/Onboarding/index.jsx"));
const Capture = lazy(() => import("./pages/Capture/index.jsx"));
const NotFound = lazy(() => import("./pages/NotFound/index.jsx"));
const Segment = lazy(() => import("./pages/Segment/index.jsx"));
// Two-pane (master-detail) Messages layout — renders the persistent conversation
// list + the active thread. Both /messages and /messages/:conversationId route
// here; the param decides the right pane. (Chunk only fetched when CHAT_ENABLED.)
// MessagesLayout internally composes the extracted ConversationListPane + Thread.
const MessagesLayout = lazy(() => import("./pages/Chat/MessagesLayout.jsx"));
// Corner mini-DM dock (D7b) — lazy so a flag-OFF build never loads its chunk.
// DmDockMount owns the (reactive) auth half of the gate and lazy-loads the dock
// itself, so an anonymous visitor on a CHAT_ENABLED build still never pulls it.
const DmDockMount = lazy(() => import("./components/ChatDock/DmDockMount"));
const Help = lazy(() => import("./pages/Help/index.jsx"));
const CreatorAnalyticsPage = lazy(() =>
  import("./pages/CreatorAnalytics/index.jsx")
);

// fix-gordon-0928-P1: "Responses should be part of Analytics, not a separate
// tab" — /responses (gordon-0923-P1-responses-section-forms-polls-locks-tabbed)
// no longer renders its own page; it redirects into the Analytics Responses
// tab on /referrals, preserving old bookmarks/shared links (mirrors
// LegacyProfileRedirect/DunningBillingRedirect above) and any `?wheel=`
// deep-link param the builder right-rail entry passes through. Pure so it's
// unit-testable without mounting the app (see routesResponsesRedirect.test.js).
export function responsesRedirectTarget(search) {
  const searchParams = new URLSearchParams(search);
  // A legacy /responses?tab=<sub-tab> link keeps its sub-tab under the
  // Responses section's own param (T28.2 BUG-1) instead of being overwritten.
  const legacyTab = searchParams.get("tab");
  if (
    ["forms", "polls", "data-entry", "paid-unlocks"].includes(legacyTab) &&
    !searchParams.has("kind")
  ) {
    searchParams.set("kind", legacyTab);
  }
  searchParams.set("tab", "responses");
  return `/referrals?${searchParams.toString()}`;
}

const ResponsesRedirect = () => {
  const location = useLocation();
  return <Navigate to={responsesRedirectTarget(location.search)} replace />;
};

// AI-03: the already-built AI Wheel Generator, given a real route. Lazy so the
// chunk is never fetched (and the route never registered) unless
// REACT_APP_AI_WHEEL_GENERATOR_ROUTE_ENABLED=true. Flip-on is gated on BE AI-01
// throttle/tier gating (AI-05) so a Free user cannot drive paid OpenAI spend.
const AiWheelGenerator = lazy(() => import("./pages/AiWheelGenerator/index.jsx"));

// WL-054: hidden v1-vs-v2 renderer parity harness. Lazy so the chunk is never
// fetched (and the route never registered) unless REACT_APP_RENDERER_V2=true.
// #1709 Gallery theming studio — flag-gated (REACT_APP_GALLERY_THEMING). Lazy so
// the chunk is never fetched (and the route never registered) unless ON.
const GalleryThemePage = lazy(() => import("./pages/GalleryTheme"));

// MOD-1 staff Moderation Console — flag-gated (REACT_APP_MODERATION_CONSOLE).
// Lazy so the chunk is never fetched (and the route never registered) unless ON.
// Server-authoritative staff-only: the BE 404s the API while its own flag is OFF
// and 403s non-staff, so the page shows the "staff access only" state to anyone
// without moderation permission — the route being mounted does not leak data.
const ModerationConsole = lazy(() => import("./pages/ModerationConsole"));

// OPS-1 staff Ops Console — flag-gated (REACT_APP_OPS_CONSOLE). Lazy so the chunk
// is never fetched (and the route never registered) unless ON. Server-authoritative
// staff-only: the BE users endpoint must 403 any non-staff caller, so the page
// shows the "staff access only" state to anyone without Ops permission — the route
// being mounted does not leak data (mirrors ModerationConsole).
const OpsConsole = lazy(() => import("./pages/OpsConsole"));

// gordon-0923-P1 hidden, DEV-ONLY, staff-only AI Wheel Lab (multimodel
// generate compare) — flag-gated (REACT_APP_AI_WHEEL_LAB_ENABLED, declared
// ONLY in DevOps-Dev.yml). Lazy so the chunk is never fetched (and the route
// never registered) unless ON — a true 404, not just an empty render.
// Server-authoritative staff-only on top: the backend 404s while its own
// AI_WHEEL_LAB_ENABLED Django setting is off and 403s any non-staff caller
// (mirrors OpsConsole/ModerationConsole).
const AiWheelLab = lazy(() => import("./pages/AiWheelLab"));

const RendererV2Compare = lazy(() => import("./pages/RendererV2Compare"));
// WL-013b: hidden View-Wheel responsive-sizing harness (renderer v2). Same flag
// as the parity harness — lazy so the chunk/route only exist when the flag is on.
const ViewWheelV2 = lazy(() => import("./pages/ViewWheelV2"));
const RendererV2Interaction = lazy(() => import("./pages/RendererV2Interaction"));

const LegacyWheelViewRedirect = () => {
  const { wheelId } = useParams();
  return <Navigate to={`/wheel/${wheelId}`} replace />;
};

// F14-4 fix (gauntlet-emailsms2, 2026-08-06, coverage/email-sms-flows.md #F14):
// payments/tasks.py:303-306 builds payment-decline dunning-recovery CTA links at
// {FRONTEND_URL}/billing/update-payment-method and /billing/retry-payment?invoice_id=...
// — neither existed as a route here (path="*" -> NotFound), so every dunning email's
// "fix your card" link 404'd. Redirect both into the real Settings > Subscriptions tab,
// preserving invoice_id as a query param for that tab to use later. Pure so it's unit-testable
// without mounting the app (see routesDunningBilling.test.js).
export function dunningRedirectTarget(search) {
  const params = new URLSearchParams(search);
  const invoiceId = params.get("invoice_id");
  return invoiceId
    ? `/settings?tab=subscriptions&invoice_id=${encodeURIComponent(invoiceId)}`
    : `/settings?tab=subscriptions`;
}

const DunningBillingRedirect = () => {
  const location = useLocation();
  return <Navigate to={dunningRedirectTarget(location.search)} replace />;
};

// [legacy removed 2026-07-14] The blue-header Profile / OthersProfile pages (years-old
// legacy, not in the design deck) are retired. Their routes now redirect to the current
// gallery (the deck's gallery / gallery-profile-card), preserving old bookmarks + shared
// links. Mirrors LegacyWheelViewRedirect above.
const LegacyProfileRedirect = () => {
  const { userid } = useParams();
  return <Navigate to={userid ? `/gallery/${userid}` : PATH.GALLERY} replace />;
};

const GalleryRoute = () => {
  const location = useLocation();
  const { id } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isLinkVault = searchParams.get("view") === "linkvault";
  const isAuthenticated = isUserLoggedIn();
  const userId = getLoggedInUser()?.id;

  if (!id && isAuthenticated && userId) {
    return <Navigate to={`/gallery/${userId}`} replace />;
  }

  if (!id && !isAuthenticated) {
    return <LoginRedirect state={{ from: location }} />;
  }

  // Show LinkVault preview only for non-authenticated users when view=linkvault
  if (isLinkVault && !isAuthenticated) {
    return <LinkVaultPage />;
  }

  // If there's an ID in the URL, allow access regardless of authentication status
  if (id) {
    return (
      <GalleryBootBoundary>
        <PageErrorBoundary>
          <Gallery />
        </PageErrorBoundary>
      </GalleryBootBoundary>
    );
  }

  // For all other cases (authenticated users or non-linkvault views)
  return (
    <PrivateRoute>
      <GalleryBootBoundary>
        <PageErrorBoundary>
          <Gallery />
        </PageErrorBoundary>
      </GalleryBootBoundary>
    </PrivateRoute>
  );
};

// ISP-9999 add-account fix: the Account Switcher's "Add account" row
// (AccountSwitcherSection.jsx handleAddAccount) navigates an ALREADY
// authenticated user to /login?addAccount=1, expecting to see the login form
// so they can sign into a second account without losing the first (PR's own
// stated intent). AuthRoute previously bounced ANY authed user straight back
// to their gallery — addAccount=1 was never read, so the button dead-ended
// (live smoke #2105, VERDICT-2105-live.md). Gated on ACCOUNT_SWITCHER_ENABLED
// so a stray/bookmarked ?addAccount=1 URL is inert while the feature is OFF —
// with the flag off (or the param absent) this is byte-identical to before.
export const AuthRoute = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = isUserLoggedIn();
  const userId = getLoggedInUser()?.id;
  const isAddAccount =
    ACCOUNT_SWITCHER_ENABLED &&
    new URLSearchParams(location.search).get("addAccount") === "1";
  // Bug #700: the Account Switcher re-auths an expired remembered account by
  // navigating the STILL-authenticated current user to
  // /login?reason=session-expired (buildReauthLoginRedirect, AccountSwitcherSection.jsx),
  // where the Login page reads the same `reason=session-expired` param to show
  // the re-auth banner (PR #4430). Without this carve-out AuthRoute bounced that
  // authed visit to Discover, so the banner was unreachable via its only real
  // trigger. Mirrors the addAccount carve-out above; additive and inert for any
  // URL lacking the param.
  const isReauth =
    new URLSearchParams(location.search).get("reason") === "session-expired";

  if (isAuthenticated && userId && !isAddAccount && !isReauth) {
    const from = location.state?.from;
    // fix-kelly-u0bfwckrj5q-...-web-staging-cleared-cache-cookies: a stale
    // tab left open on the wheel editor (/builder/:id) redirects here via
    // PrivateRoute with `from` set even on a session that never explicitly
    // navigated anywhere — logging back in then dropped the user straight
    // into edit mode instead of Discover. The editor is never something a
    // user shares/deep-links into (unlike /settings, /analytics, /messages),
    // so it's excluded from the honoured-`from` carve-out below.
    const isEditorDeepLink = from && /^\/builder(\/|$)/.test(from.pathname);
    // QA-116 item 1 / QA-17: an authenticated visitor with no explicit return-to
    // lands on the consolidated Discover surface, not their own gallery. An
    // explicit `from` (e.g. a login-gated deep link) is still honoured.
    const redirectTo =
      from && !isEditorDeepLink
        ? `${from.pathname}${from.search || ""}${from.hash || ""}`
        : PATH.DISCOVER;
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// QA-116 item 1 / QA-17: default logged-in landing is the consolidated
// Discover surface, not /gallery/:id.
// T22.219 (UX-19): a logged-out first-time visitor opening the site root also
// lands on Discover (public, carries its own LOGIN OR SIGNUP CTA) instead of a
// login wall. Explicit /login links and PrivateRoute bounces are unchanged.
export const RootRedirect = () => <Navigate to={PATH.DISCOVER} replace />;

const Routes = () => {
  const userId = getLoggedInUser()?.id;
  const isAuthenticated = isUserLoggedIn();

  const dispatch = useDispatch();

  // Use subscription sync hook to handle refresh and focus events
  useSubscriptionSync();

  useEffect(() => {
    if (isAuthenticated && userId) {
      // fix-fe-5107-own-profile-duplicate-fetch-regressed: app-shell mount is
      // passive, not a guaranteed refetch — skip if redux already has this
      // user's profile (see getOwnProfileIfNeededAction, redux/actions/user.js).
      dispatch(getOwnProfileIfNeededAction(userId));
      // fix-from-drive-verify-fe-5251-cec96289: getActiveSubscription() is
      // dispatched by useSubscriptionSync() above (mount/focus/visibility) —
      // dispatching it again here doubled every logged-in page load's
      // concurrent requests to the subscription endpoint, which gates
      // MonetizationSection and other tier-derived UI (see
      // getActiveSubscriptionData) and was measured live to intermittently
      // never resolve within a generous 25s wait under normal dev-env load.
    }
  }, [userId, isAuthenticated, dispatch]);

  return (
    <>
      {/* <Navbar /> */}

      {/* PERF-001/WEB-008 (WL-069): single Suspense boundary for every lazy
          route chunk. BrandWheelLoader is the shared, on-brand full-page loader —
          the brand Wheel filling segment by segment while the chunk downloads
          (additive swap from the generic CustomLoader spinner).
          LOADER-CENTERING LAW (Gordon, 2026-07-20): every route-level loading
          state MUST viewport-centre on both axes at a deliberate, visible size —
          never the old block-centred `page` variant, which lands near the top of
          tall/empty routes (this Suspense boundary IS exactly that top-of-page
          loader Gordon hit on Explore Wheel). `fullpage` is now unconditional
          here — no longer gated behind LOADER_V2_ENABLED, which only still gates
          the separate AI-generation line-art swap elsewhere.
          stgfix-1008-B04-r2: no white backing disc behind the jewel-wedge sweep —
          this is the SAME opt-out already applied to GallerySkeleton,
          CollaborationAccept, CollaborationJoin and SegmentBuilder's own loaders
          (kelly-1003-P2 / stgfix-1008-B04 / stgfix-1008-NEW-EditWheel-1a); this
          boundary is every lazy route chunk app-wide (incl. the invite-accept ->
          /builder/:id transition), so it carried the same latent defect. */}
      <Suspense
        fallback={<BrandWheelLoader variant="fullpage" label="Loading iYuga…" disc={false} />}
      >
        <ReactRoutes>
        <ReactRoute
          path={PATH.DASHBOARD}
          element={<RootRedirect />}
        />

        {/* ISP-9999: /notifications is a real, routed, paginated full-list page
            again (see the NotificationsPage lazy import above for why the
            QA-1014 redirect-to-Discover regressed full notification history to
            unreachable). PrivateRoute-gated like every other authenticated page
            (Analytics/Settings/Media Library) — an anonymous visitor is bounced
            to /login and returned here after signing in. */}
        <ReactRoute
          exact
          path="/notifications"
          element={
            <PrivateRoute>
              <NotificationsPage />
            </PrivateRoute>
          }
        />

        {/* DL-023 / ISP-9999: the Discover feed (ExploreV2Page) IS /discover now.
            Renamed from /explore (Gordon 2026-08-09 "Full rename incl. URL") — the
            page has been called "Discover" since 2026-08-06; the URL now matches.
            The old page component keeps its internal ExploreV2 name (URL+label
            rename only; a symbol rename is a separate, riskier change). */}
        {/* LOADER-STUTTER fix: suspend to the Discover card-skeleton, NOT the
            shared fullpage wheel, so the chunk-load affordance matches the page's
            own data-load affordance (skeleton -> skeleton -> content, never a
            wheel that is then discarded for a skeleton). */}
        <ReactRoute
          exact
          path="/discover"
          element={
            <Suspense fallback={<DiscoverFeedSkeleton />}>
              <ExploreV2Page />
            </Suspense>
          }
        />
        {/* Grace redirects so existing bookmarks / shared links never 404:
            the former primary /explore and the older shadow /explore-v2 both
            land on the new /discover. `replace` so Back doesn't bounce. */}
        <ReactRoute
          exact
          path="/explore"
          element={<Navigate to="/discover" replace />}
        />
        <ReactRoute
          exact
          path="/explore-v2"
          element={<Navigate to="/discover" replace />}
        />

        {/* Follower Feed (Wheel-as-a-Room rung 3) — "Following" feed of public
            wheels from creators you follow. Flag-gated (REACT_APP_FOLLOWER_FEED):
            when OFF the route is not mounted and the page chunk is never loaded. */}
        {FOLLOWER_FEED_ENABLED && (
          <ReactRoute
            exact
            path="/following"
            element={
              <PrivateRoute>
                <FollowerFeedPage />
              </PrivateRoute>
            }
          />
        )}

        <ReactRoute
          exact
          path={PATH.ANALYTICS}
          element={
            <PrivateRoute>
              <CreatorAnalyticsPage />
            </PrivateRoute>
          }
        />

        {/* gordon-0923-P1-responses-section-forms-polls-locks-tabbed — flag-gated
            (OFF = route unregistered, falls through to the 404 catch-all). Owner-only
            (PrivateRoute); deep-linkable via ?wheel=. Redirects into the Analytics
            Responses tab (fix-gordon-0928-P1) rather than rendering its own page. */}
        {RESPONSES_SECTION_ENABLED && (
          <ReactRoute
            exact
            path={PATH.RESPONSES}
            element={
              <PrivateRoute>
                <ResponsesRedirect />
              </PrivateRoute>
            }
          />
        )}


        <ReactRoute exact path={PATH.MY_PROFILE} element={<LegacyProfileRedirect />} />

        <ReactRoute
          exact
          path={"/wheel/:wheelId"}
          element={
            <PageErrorBoundary>
              {/* T13.1: per-route fallback = the SAME shell ViewWheel shows
                  while its wheel data loads (header bar + fullpage brand
                  loader), so chunk-load -> data-load never drops to a blank
                  white page. Same loader props as the shared fallback below. */}
              <Suspense
                fallback={
                  <ViewWheelLoadingShell label="Loading iYuga…" disc={false} />
                }
              >
                <ViewWheel />
              </Suspense>
            </PageErrorBoundary>
          }
        />

        <ReactRoute
          exact
          path={"/wheel/segment/:id"}
          element={
            <PageErrorBoundary>
              <Segment />
            </PageErrorBoundary>
          }
        />

        <ReactRoute exact path={PATH.PROFILE} element={<LegacyProfileRedirect />} />

        <ReactRoute
          exact
          path={"/privacy-policy"}
          element={<PrivacyPolicy />}
        />

        <ReactRoute exact path={"/t&c"} element={<Terms />} />
        <ReactRoute
          exact
          path={PATH.OTHER_USER_PROFILE}
          element={<LegacyProfileRedirect />}
        />

        <ReactRoute
          exact
          path={PATH.LOGIN}
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />
        <ReactRoute
          exact
          path={PATH.SIGNUP}
          element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          }
        />
        <ReactRoute
          exact
          path={`${PATH.SIGNUP}/:referralCode`}
          element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          }
        />
        <ReactRoute
          exact
          path={PATH.FORGOT_PASSWORD}
          element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          }
        />
        {/* Login L4 -- passwordless email magic-link (flag-gated, see
            magicLinkFlag.js). OFF = both routes unregistered, falling through
            to the catch-all NotFound, byte-identical to today. The redeem
            landing route is intentionally NOT wrapped in AuthRoute/PrivateRoute
            -- a visitor following the emailed link is by definition
            unauthenticated when they arrive. */}
        {MAGIC_LINK_LOGIN_ENABLED && (
          <ReactRoute
            exact
            path={PATH.MAGIC_LINK_REQUEST}
            element={
              <AuthRoute>
                <RequestMagicLink />
              </AuthRoute>
            }
          />
        )}
        {MAGIC_LINK_LOGIN_ENABLED && (
          <ReactRoute
            exact
            path={PATH.MAGIC_LINK_LOGIN}
            element={<MagicLinkLogin />}
          />
        )}

        {/*
          PATH.RESET_PASSWORD ("/resetpassword") is defined in config.js but was
          never routed, so the link 404'd (fidelity audit 2026-07-13, R3). The
          active password-reset flow is the OTP-based ForgotPassword flow, whose
          final step is the "Enter new password" (New/Confirm + Reset) screen —
          so we resolve /resetpassword to that same component instead of a 404.
        */}
        <ReactRoute
          exact
          path={PATH.RESET_PASSWORD}
          element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          }
        />

        <ReactRoute exact path="/privacypolicy" element={<PrivacyPolicy />} />
        <ReactRoute
          exact
          path="/referrals/:status"
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />
        <ReactRoute
          exact
          path="/referrals"
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />

        <ReactRoute
          exact
          path="/invite"
          element={
            <PrivateRoute>
              <Invite />
            </PrivateRoute>
          }
        />

        <ReactRoute exact path="/profile" element={<LegacyProfileRedirect />} />
        <ReactRoute
          path="/wheelview/:wheelId/*"
          element={<LegacyWheelViewRedirect />}
        />
        <ReactRoute
          path={PATH.VIEW_WHEEL}
          element={<LegacyWheelViewRedirect />}
        />

        <ReactRoute
          exact
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />

        {/* F14-4 fix (gauntlet-emailsms2, 2026-08-06): the two dunning-email CTA
            destinations. Both were previously undeclared -> path="*" -> NotFound. */}
        <ReactRoute
          exact
          path="/billing/update-payment-method"
          element={
            <PrivateRoute>
              <DunningBillingRedirect />
            </PrivateRoute>
          }
        />
        <ReactRoute
          exact
          path="/billing/retry-payment"
          element={
            <PrivateRoute>
              <DunningBillingRedirect />
            </PrivateRoute>
          }
        />

        {/* Teams / Workspaces (Roadmap #8). Flag-gated: while
            REACT_APP_TEAMS_ENABLED is OFF the route 404s (byte-identical to
            today). Base OFF; staging sets "true". */}
        <ReactRoute
          exact
          path="/teams"
          element={
            TEAMS_ENABLED ? (
              <PrivateRoute>
                <Teams />
              </PrivateRoute>
            ) : (
              <NotFound />
            )
          }
        />

        {/* Teams / Workspaces (Roadmap #8) — invite-accept landing. The backend
            invite email points at `/invite/accept?token=...`
            (workspaces/services/invite_email.py INVITE_ACCEPT_PATH); before this
            route existed the emailed link 404'd and no invitee could ever
            become an active member. Flag-gated (REACT_APP_TEAMS_ENABLED OFF ->
            404, byte-identical to today) and wrapped in PrivateRoute so the
            invitee logs in first (Login preserves from.search, so ?token=
            survives; the server also binds the invite to the invited email).
            stgfix-1008-P0-6601: PrivateRoute also durably remembers this path
            for a Signup (not just Login) detour — see the route list in
            PrivateRoute.js, not a prop here, so this wrapper line stays
            byte-identical to Usman's 2026-09-28 guard (fa7371c3a). */}
        <ReactRoute
          exact
          path="/invite/accept"
          element={
            TEAMS_ENABLED ? (
              <PrivateRoute>
                <InviteAccept />
              </PrivateRoute>
            ) : (
              <NotFound />
            )
          }
        />

        {/* Wheel Collaboration (P4) — invite-accept landing. Flag-gated: while
            REACT_APP_WHEEL_COLLABORATION is OFF the route 404s (byte-identical to
            today). Base OFF; staging sets "true". Wrapped in PrivateRoute so the
            invitee logs in first (server also requires the email to match).
            stgfix-1008-P0-6601: see /invite/accept above — same Signup-survival
            reasoning, same PrivateRoute.js route list, no prop. */}
        <ReactRoute
          exact
          path="/collaboration/accept"
          element={
            WHEEL_COLLABORATION_ENABLED ? (
              <PrivateRoute>
                <CollaborationAccept />
              </PrivateRoute>
            ) : (
              <NotFound />
            )
          }
        />

        {/* Wheel Collaboration (P4b) — shareable-link JOIN landing. Same flag +
            PrivateRoute gating as the accept route; a logged-out visitor logs in
            or signs up first and returns here (Login honours state.from), then
            joins. The token is email-agnostic (anyone with the link may join).
            stgfix-1008-P0-6601: see /invite/accept above — same Signup-survival
            reasoning, same PrivateRoute.js route list, no prop. */}
        <ReactRoute
          exact
          path="/collaboration/join"
          element={
            WHEEL_COLLABORATION_ENABLED ? (
              <PrivateRoute>
                <CollaborationJoin />
              </PrivateRoute>
            ) : (
              <NotFound />
            )
          }
        />

        {/* LOADER-STUTTER fix: Gallery's own initial-load affordance IS the hero
            <GallerySkeleton> wheel (skipEntry). Suspend the route chunk to that
            SAME loader instead of the shared fullpage wheel, so the chunk-load ->
            data-load handoff is one continuous hero wheel — no fullpage->hero
            size jump / re-mount blink. */}
        <ReactRoute
          exact
          path={PATH.GALLERY}
          element={
            <Suspense fallback={<GallerySkeleton />}>
              <GalleryRoute />
            </Suspense>
          }
        />

        <ReactRoute
          exact
          path={PATH.USER_GALLERY}
          element={
            <Suspense fallback={<GallerySkeleton />}>
              <GalleryRoute />
            </Suspense>
          }
        />

        {/* BUG-953: shareable Collection deep link -- resolves the owner and
            forwards into PATH.USER_GALLERY's existing `?collection=<id>`
            handling (see CollectionRedirect.jsx). */}
        <ReactRoute
          exact
          path={PATH.COLLECTION}
          element={<CollectionRedirect />}
        />

        <ReactRoute
          exact
          path={PATH.MEDIA_LIBRARY}
          element={
            <PrivateRoute>
              <MediaLibrary />
            </PrivateRoute>
          }
        />

        <ReactRoute exact path={PATH.ONBOARDING} element={<Onboarding />} />

        {/* Capture / Notepad inbox (WL-021, backend #1182) — gated behind
            CAPTURE_ENABLED (default OFF) so the route is unregistered and the
            surface unreachable until the flag is flipped. */}
        {CAPTURE_ENABLED && (
          <ReactRoute
            exact
            path={PATH.CAPTURE}
            element={
              <PrivateRoute>
                <Capture />
              </PrivateRoute>
            }
          />
        )}
        {/* Direct messages (DM / chat) — gated behind CHAT_ENABLED (default OFF).
            Routes are only registered when the flag is on, so they are
            unreachable otherwise (the surface is indistinguishable from absent).
            ISP-9999 fe-dev-canvasjs-defer-and-messages-early-fetch: MessagesLayout
            gets its OWN nested Suspense, with the SAME BrandWheelLoader fallback
            the shared outer boundary already used (no loading-affordance change —
            see routes.messagesBrandWheelLoaderBoundary.test.js), so
            MessagesRoutePrefetch — a sibling, NOT inside that nested boundary —
            mounts and fires its effect in the first commit, without waiting on
            the lazy chunk. */}
        {CHAT_ENABLED && (
          <ReactRoute
            exact
            path={PATH.MESSAGES}
            element={
              <PrivateRoute>
                <MessagesRoutePrefetch key="messages-route-prefetch" />
                <Suspense
                  key="messages-layout"
                  fallback={
                    <BrandWheelLoader variant="fullpage" label="Loading iYuga…" disc={false} />
                  }
                >
                  <MessagesLayout />
                </Suspense>
              </PrivateRoute>
            }
          />
        )}
        {CHAT_ENABLED && (
          <ReactRoute
            exact
            path={PATH.MESSAGES_THREAD}
            element={
              <PrivateRoute>
                <MessagesRoutePrefetch key="messages-route-prefetch" />
                <Suspense
                  key="messages-layout"
                  fallback={
                    <BrandWheelLoader variant="fullpage" label="Loading iYuga…" disc={false} />
                  }
                >
                  <MessagesLayout />
                </Suspense>
              </PrivateRoute>
            }
          />
        )}

        <ReactRoute
          exact
          path="/builder/:id?"
          element={
            <PrivateRoute>
              <SegmentBuilder />
            </PrivateRoute>
          }
        />

        {/* Help / FAQ / Support (#14). Public — help must be reachable when
            logged out. HELP_UNIFIED_ENABLED OFF (default): byte-identical to
            today — /help, /faq and /support all render the same <Help/> surface
            and /contact does not exist. ON: /help is the single unified page and
            the old routes collapse into it — /faq -> #faq section, /support +
            /contact -> #contact section (targets are the tested
            HELP_UNIFIED_REDIRECTS map). */}
        <ReactRoute exact path={PATH.HELP} element={<Help />} />
        <ReactRoute
          exact
          path={PATH.FAQ}
          element={
            HELP_UNIFIED_ENABLED ? (
              <Navigate to={HELP_UNIFIED_REDIRECTS[PATH.FAQ]} replace />
            ) : (
              <Help />
            )
          }
        />
        <ReactRoute
          exact
          path={PATH.SUPPORT}
          element={
            HELP_UNIFIED_ENABLED ? (
              <Navigate to={HELP_UNIFIED_REDIRECTS[PATH.SUPPORT]} replace />
            ) : (
              <Help />
            )
          }
        />
        {HELP_UNIFIED_ENABLED && (
          <ReactRoute
            exact
            path="/contact"
            element={
              <Navigate to={HELP_UNIFIED_REDIRECTS["/contact"]} replace />
            }
          />
        )}

        {/* AI-03 AI Wheel Generator — flag-gated (OFF = route unregistered,
            falls through to the 404 catch-all). Flip-on gated on BE AI-01/AI-05
            (throttle + per-tier token/rate enforcement) so a Free user cannot
            drive billable OpenAI spend. */}
        {AI_WHEEL_GENERATOR_ROUTE_ENABLED && (
          <ReactRoute
            exact
            path={PATH.AI_WHEEL_GENERATOR}
            element={
              <PrivateRoute>
                <AiWheelGenerator />
              </PrivateRoute>
            }
          />
        )}

        {/* #1709 Gallery theming studio — flag-gated (OFF = route unregistered,
            falls through to /gallery/:id then catch-all). Static "/gallery/theme"
            out-ranks the "/gallery/:id" param route in React Router v6. */}
        {GALLERY_THEMING_ENABLED && (
          <ReactRoute
            exact
            path={PATH.GALLERY_THEME}
            element={
              <PrivateRoute>
                <PageErrorBoundary>
                  <GalleryThemePage />
                </PageErrorBoundary>
              </PrivateRoute>
            }
          />
        )}

        {/* MOD-1 staff Moderation Console — flag-gated (OFF = route unregistered,
            falls through to the 404 catch-all). Wrapped in PrivateRoute so a
            logged-in session is required; server enforces staff-only on top. */}
        {MODERATION_CONSOLE_ENABLED && (
          <ReactRoute
            exact
            path={PATH.MODERATION_CONSOLE}
            element={
              <PrivateRoute>
                <PageErrorBoundary>
                  <ModerationConsole />
                </PageErrorBoundary>
              </PrivateRoute>
            }
          />
        )}

        {/* OPS-1 staff Ops Console — flag-gated (OFF = route unregistered, falls
            through to the 404 catch-all). Wrapped in PrivateRoute so a logged-in
            session is required; server enforces staff-only on top. */}
        {OPS_CONSOLE_ENABLED && (
          <ReactRoute
            exact
            path={PATH.OPS_CONSOLE}
            element={
              <PrivateRoute>
                <PageErrorBoundary>
                  <OpsConsole />
                </PageErrorBoundary>
              </PrivateRoute>
            }
          />
        )}

        {/* gordon-0923-P1 hidden, DEV-ONLY, staff-only AI Wheel Lab — flag-gated
            (OFF = route unregistered, falls through to the 404 catch-all). No
            nav link anywhere. Wrapped in PrivateRoute so a logged-in session
            is required; the backend enforces staff-only + dev-only on top
            (403 non-staff, 404 while its own flag is off — mirrors OpsConsole). */}
        {AI_WHEEL_LAB_ENABLED && (
          <ReactRoute
            exact
            path={PATH.AI_WHEEL_LAB}
            element={
              <PrivateRoute>
                <PageErrorBoundary>
                  <AiWheelLab />
                </PageErrorBoundary>
              </PrivateRoute>
            }
          />
        )}

        {/* WL-054 renderer-v2 parity harness — flag-gated (OFF = 404). */}
        {RENDERER_V2_ENABLED && (
          <ReactRoute
            exact
            path="/renderer-v2-compare"
            element={
              <Suspense fallback={null}>
                <RendererV2Compare />
              </Suspense>
            }
          />
        )}

        {/* WL-013b View-Wheel responsive-sizing harness — same flag (OFF = 404). */}
        {RENDERER_V2_ENABLED && (
          <ReactRoute
            exact
            path="/renderer-v2-view"
            element={
              <Suspense fallback={null}>
                <ViewWheelV2 />
              </Suspense>
            }
          />
        )}

        {/* rv2 View-interaction harness (gif hover/select + selection
            indicator candidates) — same flag (OFF = 404). */}
        {RENDERER_V2_ENABLED && (
          <ReactRoute
            exact
            path="/renderer-v2-interaction"
            element={
              <Suspense fallback={null}>
                <RendererV2Interaction />
              </Suspense>
            }
          />
        )}

        {/* Catch-all route for 404 - must be last */}
        <ReactRoute path="*" element={<NotFound />} />
      </ReactRoutes>
      </Suspense>

      {/* DL-098 — THE SINGLE MOUNT of the anonymous login gate.
          `openNonAuthenticatedModal` is dispatched from a SHARED primitive
          (`useUserRelationship`, `WheelCardThreeDotMenu`, `FollowersFollowingModal`,
          `WheelGallery/Profile`, `ViewWheel`), but the modal that listens for it
          used to be mounted by only TWO pages — Gallery and ViewWheel. Every other
          surface dispatched into a void.
          MEASURED, anonymous, on web-stg 2026-08-01, bundle main.f563cd08.js:
          Follow in an /explore card kebab produced no gate, no toast, no redirect
          (bodyDelta -15, i.e. only the menu closing) while the SAME action on
          /wheel/65 produced the gate — a known-good control proving the probe, not
          the page. Discover is the acquisition surface; the one control that asks
          an anonymous visitor to sign up was inert there.
          Mounted here, once, so the gate is reachable from every route. Patching
          the primitive rather than the call sites is G-118: a per-page mount is
          only ever as complete as the grep that found the pages.
          The two page-local mounts are REMOVED in the same commit — two live
          instances of a redux-driven modal would both open and stack. */}
      <NonAuthenticatedModal />

      {/* Corner mini-DM dock (D7b) — persistent bottom-right, over the same DM
          data as /messages. Gated behind CHAT_ENABLED + auth; when the flag is
          OFF nothing renders and its chunk is never loaded (byte-identical).
          The auth half of the gate lives in DmDockMount because `isAuthenticated`
          here is a one-shot localStorage read that never flips after a
          client-side login — which left the header Messages icon inert until the
          user reloaded (measured on web-stg 2026-07-28; see DmDockMount.jsx). */}
      {CHAT_ENABLED && (
        <Suspense fallback={null}>
          <DmDockMount />
        </Suspense>
      )}
    </>
  );
};

export default Routes;
